import { collection, documentId, getDocs, query, QuerySnapshot, where } from 'firebase/firestore'
import {
  GATE_PASS_NEEDED_CUTOFF_DATE,
  Vehicle,
  vehicleNeedsGatePass,
  type AuctionGatePassLookup,
} from '@otw/models'
import {
  getRepairTruckWorkStatus,
  getTruckStatus,
  type RepairTruck,
  type ShipmentPlan,
  type TransportTruck,
} from '@otw/models/logistics'
import {
  repairTruckConverter,
  shipmentPlanConverter,
  transportTruckConverter,
  vehicleConverter,
} from '@otw/models/converters'
import { db } from 'config/firebase'
import { isPickupLocationComplete, vehicleNeedsPickupAddress } from './actionBannerFilters'

export interface RepairTruckNeedingTransporter {
  repairTruck: RepairTruck
  transportTruck: TransportTruck
  shopId: string
  vin: string
  label: string
  stockNumber?: string
  customerName?: string
}

export { isPickupLocationComplete, vehicleNeedsPickupAddress }

export const ACTION_BANNER_QUERY_ROOT = 'action-banner' as const

/** Navbar action alerts — match dashboard in-session freshness (10m). */
export const ACTION_BANNER_STALE_MS = 1000 * 60 * 10

export const ACTION_BANNER_GC_MS = 1000 * 60 * 60

export const actionBannerPickupQueryKey = [ACTION_BANNER_QUERY_ROOT, 'pickup-address'] as const

export const actionBannerGatePassQueryKey = [ACTION_BANNER_QUERY_ROOT, 'gate-pass'] as const

export const actionBannerTransporterQueryKey = [ACTION_BANNER_QUERY_ROOT, 'transporter'] as const

const masterInventoryCollection = () =>
  collection(db, 'master_inventory').withConverter(vehicleConverter)

const mapVehicleSnapshot = (snapshot: QuerySnapshot<Vehicle>): Vehicle[] =>
  snapshot.docs.map(docSnap => ({
    ...docSnap.data(),
    id: docSnap.id,
  }))

export const fetchVehiclesNeedingPickupAddress = async (): Promise<Vehicle[]> => {
  const snapshot = await getDocs(
    query(masterInventoryCollection(), where('ready_for_transport.needs_address', '==', true)),
  )
  return mapVehicleSnapshot(snapshot).filter(vehicleNeedsPickupAddress)
}

const fetchAuctionGatePassLookup = async (): Promise<Map<string, AuctionGatePassLookup>> => {
  const snapshot = await getDocs(collection(db, 'auctions'))
  const map = new Map<string, AuctionGatePassLookup>()
  for (const auctionDoc of snapshot.docs) {
    const data = auctionDoc.data()
    map.set(auctionDoc.id, { gate_passes_needed: data.gate_passes_needed })
  }
  return map
}

export const fetchVehiclesNeedingGatePass = async (): Promise<Vehicle[]> => {
  const [snapshot, auctionLocationsById] = await Promise.all([
    getDocs(
      query(
        masterInventoryCollection(),
        where('seller_type', '==', 'Auction'),
        where('paid_for', '==', true),
        where('paid_for_date', '>=', GATE_PASS_NEEDED_CUTOFF_DATE),
      ),
    ),
    fetchAuctionGatePassLookup(),
  ])
  return mapVehicleSnapshot(snapshot).filter(vehicle =>
    vehicleNeedsGatePass(vehicle, auctionLocationsById),
  )
}

function hasOutboundRepairLegWithCompany(
  plans: ShipmentPlan[],
  transportTruckId: string,
  repairShopLocationId: string,
): boolean {
  for (const plan of plans) {
    if (plan.transport_truck_id !== transportTruckId) continue
    for (const leg of plan.shipment_legs ?? []) {
      if (
        leg.from_type === 'repair' &&
        leg.from_id === repairShopLocationId &&
        Boolean(leg.company_id)
      ) {
        return true
      }
    }
  }
  return false
}

async function fetchVehiclesByIds(ids: string[]): Promise<Map<string, Vehicle>> {
  const map = new Map<string, Vehicle>()
  const unique = [...new Set(ids.filter(Boolean))]
  for (let i = 0; i < unique.length; i += 10) {
    const chunk = unique.slice(i, i + 10)
    const snapshot = await getDocs(
      query(masterInventoryCollection(), where(documentId(), 'in', chunk)),
    )
    for (const docSnap of snapshot.docs) {
      map.set(docSnap.id, { ...docSnap.data(), id: docSnap.id })
    }
  }
  return map
}

export const fetchRepairTrucksNeedingTransporter = async (): Promise<
  RepairTruckNeedingTransporter[]
> => {
  const transportCol = collection(db, 'transport_trucks').withConverter(transportTruckConverter)
  const plansCol = collection(db, 'shipment_plans').withConverter(shipmentPlanConverter)
  const repairCol = collection(db, 'repair_trucks').withConverter(repairTruckConverter)

  const [transportSnap, plansSnap, repairSnap] = await Promise.all([
    getDocs(query(transportCol)),
    getDocs(query(plansCol)),
    getDocs(query(repairCol)),
  ])

  const transportTrucks = transportSnap.docs.map(d => d.data())
  const shipmentPlans = plansSnap.docs.map(d => d.data())
  const repairTrucks = repairSnap.docs.map(d => d.data())

  const trucksById = new Map(transportTrucks.map(truck => [truck.id, truck]))
  const candidateVehicleIds: string[] = []

  for (const repairTruck of repairTrucks) {
    if (getRepairTruckWorkStatus(repairTruck) !== 'complete') continue
    const transportTruck = trucksById.get(repairTruck.transport_truck_id)
    if (!transportTruck) continue
    const stillAtShop =
      getTruckStatus(transportTruck) === 'repair' &&
      transportTruck.currentLocationId === repairTruck.repair_shop_location_id
    if (!stillAtShop) continue
    if (
      hasOutboundRepairLegWithCompany(
        shipmentPlans,
        transportTruck.id,
        repairTruck.repair_shop_location_id,
      )
    ) {
      continue
    }
    if (transportTruck.vehicle_id) {
      candidateVehicleIds.push(transportTruck.vehicle_id)
    }
  }

  const vehiclesById = await fetchVehiclesByIds(candidateVehicleIds)
  const result: RepairTruckNeedingTransporter[] = []

  for (const repairTruck of repairTrucks) {
    if (getRepairTruckWorkStatus(repairTruck) !== 'complete') continue
    const transportTruck = trucksById.get(repairTruck.transport_truck_id)
    if (!transportTruck) continue
    const stillAtShop =
      getTruckStatus(transportTruck) === 'repair' &&
      transportTruck.currentLocationId === repairTruck.repair_shop_location_id
    if (!stillAtShop) continue
    if (
      hasOutboundRepairLegWithCompany(
        shipmentPlans,
        transportTruck.id,
        repairTruck.repair_shop_location_id,
      )
    ) {
      continue
    }

    const vehicle = transportTruck.vehicle_id
      ? vehiclesById.get(transportTruck.vehicle_id)
      : undefined
    const nameLabel = vehicle
      ? `${vehicle.year ?? ''} ${vehicle.make ?? ''} ${vehicle.model ?? ''}`.trim()
      : ''
    const vin = transportTruck.vin || vehicle?.vin || ''
    const label = nameLabel || (vin ? `VIN: ${vin}` : 'Unknown vehicle')
    const stockNumber = vehicle?.stock_number?.trim() || undefined
    const customerName =
      vehicle?.selling_to_user?.name?.trim() ||
      vehicle?.sold_to_user?.name?.trim() ||
      undefined

    result.push({
      repairTruck,
      transportTruck,
      shopId: repairTruck.repair_shop_location_id,
      vin,
      label,
      stockNumber,
      customerName,
    })
  }

  return result.sort((a, b) => a.label.localeCompare(b.label))
}
